import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { noop, ytAllowlistStartDate, } from "./detection.types.js";
import { youTubeAlreadyAllowListed, youTubeAutoAllowlisted, youTubeWallDetected, youTubeNavigation, ytRecordEvent, contextTamperingEvent, adShownEvent, errorEvent, ErrorType, } from "../shared/index.js";
const captureStartDate = function (parameters) {
    if (parameters.prefs.get(ytAllowlistStartDate) === 0) {
        parameters.prefs.set(ytAllowlistStartDate, Date.now());
        parameters.logger.debug("[yt-detection]: set start date", new Date(parameters.prefs.get(ytAllowlistStartDate)).toLocaleDateString());
    }
};
const captureDateOnUpdate = (parameters, details) => {
    if (details.reason === "update" || details.reason === "install") {
        captureStartDate(parameters);
    }
};
async function hasAllowlistedFilter(tabId) {
    let isAllowlisted = false;
    if (typeof tabId !== "undefined") {
        const filters = await ewe.filters.getAllowingFilters(tabId);
        isAllowlisted = filters.some((f) => f.includes("youtube.com"));
    }
    return isAllowlisted;
}
const processYouTubeWallMessage = async (parameters, message, sender) => {
    var _a, _b;
    const tabId = (_a = sender.tab) === null || _a === void 0 ? void 0 : _a.id;
    const tabURL = (_b = sender.tab) === null || _b === void 0 ? void 0 : _b.url;
    if (typeof tabId === "undefined" || typeof tabURL === "undefined") {
        return;
    }
    const { sendAdWallEvents = noop } = parameters;
    const senderURL = new URL(tabURL);
    const host = senderURL.hostname.replace(/^www\./, "");
    const allowlistUrl = host;
    const allowlistDuration = "tab";
    const filters = await ewe.filters.getAllowingFilters(tabId);
    const isAllowListed = filters.some((f) => f.includes(allowlistUrl));
    const eventName = isAllowListed
        ? youTubeAlreadyAllowListed
        : youTubeWallDetected;
    sendAdWallEvents(eventName, {
        category: message.hasDismiss ? "soft" : "hard",
        userLoggedIn: message.userLoggedIn ? "1" : "0",
        isAllowListed: isAllowListed ? "1" : "0",
        allowlistDuration,
    });
    if (isAllowListed) {
        return;
    }
    const ruleText = `@@||${allowlistUrl}^$document`;
    const metadata = {
        expiresByTabId: tabId,
        origin: "yt-auto",
        created: Date.now(),
    };
    await ewe.filters.add(ruleText, metadata);
    if (message.currentPlaybackTime > 5) {
        senderURL.searchParams.set("t", Math.floor(message.currentPlaybackTime).toString());
        await browser.tabs.update(tabId, { url: senderURL.href });
    }
    else {
        await browser.tabs.reload(tabId);
    }
    sendAdWallEvents(youTubeAutoAllowlisted, {
        allowlistDuration,
    });
};
export function start(parameters) {
    const { sendAdWallEvents = noop } = parameters;
    const captureDateOnUpdatedWithParameters = captureDateOnUpdate.bind(null, parameters);
    browser.runtime.onInstalled.addListener(captureDateOnUpdatedWithParameters);
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { url, tabId } = details;
        const parsedUrl = new URL(url);
        const errorMsg = parsedUrl.searchParams.get("msg");
        const ytClientVersion = parsedUrl.searchParams.get("client.version");
        const stack = parsedUrl.searchParams.get("stack");
        const type = parsedUrl.searchParams.get("type");
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            parameters.isPremiumActive(),
        ]);
        if ((errorMsg === null || errorMsg === void 0 ? void 0 : errorMsg.includes("read-only")) &&
            (errorMsg === null || errorMsg === void 0 ? void 0 : errorMsg.includes("non-configurable"))) {
            sendAdWallEvents(contextTamperingEvent, {
                applicationVersion: ytClientVersion,
                isAllowListed: isAllowlisted ? "1" : "0",
                p: isPremium,
            });
        }
        else {
            sendAdWallEvents(errorEvent, {
                isAllowListed: isAllowlisted ? "1" : "0",
                p: isPremium,
                error: type,
                applicationVersion: ytClientVersion,
                category: ErrorType.UNCAUGHT_EXCEPTION,
                error_msg: stack,
            });
        }
    }, { urls: ["*://*.youtube.com/error_204*"] });
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { url, tabId } = details;
        const parsedUrl = new URL(url);
        const isFinalEvent = parsedUrl.searchParams.get("final");
        const totalAdTime = parsedUrl.searchParams.get("len");
        const adShownTime = parsedUrl.searchParams.get("rt");
        const adFormat = parsedUrl.searchParams.get("adformat");
        const contentVideo = parsedUrl.searchParams.get("content_v");
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            parameters.isPremiumActive(),
        ]);
        if (isFinalEvent !== "1" ||
            !totalAdTime ||
            !adShownTime ||
            !contentVideo) {
            return;
        }
        sendAdWallEvents(adShownEvent, {
            isAllowListed: isAllowlisted ? "1" : "0",
            p: isPremium,
            category: adFormat,
            bc: Math.floor(Number(totalAdTime) * 1000),
            amount: Math.floor(Number(adShownTime) * 1000),
        });
    }, { urls: ["*://*.youtube.com/api/stats/watchtime?*el=adunit*"] });
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { url, tabId } = details;
        const parsedUrl = new URL(url);
        const error = parsedUrl.searchParams.get("error");
        const clientVersion = parsedUrl.searchParams.get("cver");
        if (!error) {
            return;
        }
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            parameters.isPremiumActive(),
        ]);
        sendAdWallEvents(errorEvent, {
            isAllowListed: isAllowlisted ? "1" : "0",
            p: isPremium,
            error,
            applicationVersion: clientVersion,
            category: ErrorType.SWW_ERROR,
            error_msg: "",
        });
    }, { urls: ["*://*.youtube.com/api/stats/qoe?*"] });
    const processYouTubeWallMessageWithParameters = processYouTubeWallMessage.bind(null, parameters);
    parameters.port.on(youTubeWallDetected, processYouTubeWallMessageWithParameters);
    parameters.port.on(youTubeNavigation, async (message, sender) => {
        sendAdWallEvents(youTubeNavigation, {
            userLoggedIn: message.userLoggedIn ? "1" : "0",
        });
    });
    parameters.port.on(ytRecordEvent, async ({ eventName, eventPayload }, sender) => {
        var _a;
        const tabId = (_a = sender.tab) === null || _a === void 0 ? void 0 : _a.id;
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            parameters.isPremiumActive(),
        ]);
        sendAdWallEvents(eventName, {
            ...eventPayload,
            isAllowListed: isAllowlisted ? "1" : "0",
            p: isPremium,
        });
    });
    const trustedEventNames = [
        youTubeWallDetected,
        youTubeNavigation,
        ytRecordEvent,
    ];
    parameters.addTrustedMessageTypes("https://youtube.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://www.youtube.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://youtube-nocookie.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://www.youtube-nocookie.com", trustedEventNames);
}
